import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, MessageSquare, Users, Lock, Send, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

type Status = "open" | "pending" | "answered" | "closed";
type Route = "gathering" | "private";

interface GatheringQuestion {
  id: string;
  user_id: string;
  question_text: string;
  status: Status;
  route: Route;
  answer_text: string | null;
  answered_at: string | null;
  answered_by: string | null;
  internal_notes: string | null;
  call_reference: string | null;
  notified_at: string | null;
  created_at: string;
  updated_at: string;
  ticket_id: string | null;
}

interface ProfileLite {
  id: string;
  full_name: string | null;
  email: string | null;
}

interface TicketLite {
  id: string;
  helpscout_conversation_id: string | null;
}

const STATUS_LABEL: Record<Status, string> = {
  open: "Waiting for the congregation",
  pending: "Coming soon",
  answered: "Answered",
  closed: "Not taken up",
};

interface EditState {
  answer: string;
  internal: string;
  callRef: string;
  status: Status;
}

export default function AdminGatheringQuestions() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [edits, setEdits] = useState<Record<string, EditState>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [notifyingId, setNotifyingId] = useState<string | null>(null);

  const { data: questions = [], isLoading } = useQuery({
    queryKey: ["admin-gathering-questions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("congregation_questions")
        .select("*")
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as GatheringQuestion[];
    },
  });

  const userIds = useMemo(() => Array.from(new Set(questions.map((q) => q.user_id))), [questions]);
  const ticketIds = useMemo(
    () => Array.from(new Set(questions.map((q) => q.ticket_id).filter((x): x is string => !!x))),
    [questions],
  );

  const { data: profiles = [] } = useQuery({
    queryKey: ["admin-gathering-profiles", userIds.join(",")],
    enabled: userIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .in("id", userIds);
      if (error) throw error;
      return (data ?? []) as ProfileLite[];
    },
  });

  const { data: tickets = [] } = useQuery({
    queryKey: ["admin-gathering-tickets", ticketIds.join(",")],
    enabled: ticketIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("support_tickets")
        .select("id, helpscout_conversation_id")
        .in("id", ticketIds);
      if (error) throw error;
      return (data ?? []) as TicketLite[];
    },
  });

  const profileById = new Map(profiles.map((p) => [p.id, p]));
  const ticketById = new Map(tickets.map((t) => [t.id, t]));

  const active = questions.filter((q) => q.status === "open" || q.status === "pending");
  const gatheringQ = active.filter((q) => q.route === "gathering");
  const privateQ = active.filter((q) => q.route === "private");
  const history = questions
    .filter((q) => q.status === "answered" || q.status === "closed")
    .sort((a, b) => (b.answered_at ?? b.updated_at).localeCompare(a.answered_at ?? a.updated_at));

  const getEdit = (q: GatheringQuestion): EditState => (
    edits[q.id] ?? {
      answer: q.answer_text ?? "",
      internal: q.internal_notes ?? "",
      callRef: q.call_reference ?? "",
      status: q.status,
    }
  );
  const patchEdit = (id: string, patch: Partial<EditState>) =>
    setEdits((e) => ({ ...e, [id]: { ...(e[id] ?? { answer: "", internal: "", callRef: "", status: "open" as Status }), ...patch } }));

  const persistAdminFields = async (q: GatheringQuestion, next: Partial<EditState>) => {
    const patch: Record<string, unknown> = {};
    if (next.internal !== undefined) patch.internal_notes = next.internal || null;
    if (next.callRef !== undefined) patch.call_reference = next.callRef || null;
    if (next.status !== undefined) patch.status = next.status;
    if (Object.keys(patch).length === 0) return;
    const { error } = await supabase.from("congregation_questions").update(patch).eq("id", q.id);
    if (error) throw error;
  };

  const triggerNotify = async (questionId: string) => {
    const { data, error } = await supabase.functions.invoke("notify-question-answered", {
      body: { question_id: questionId },
    });
    if (error) throw error;
    return data as {
      ok: boolean;
      route?: "gathering" | "private";
      helpscout?: { ok?: boolean; skipped?: string; error?: string; conversation_id?: string; status?: string };
      portal_notified: boolean;
    };
  };

  const describeDelivery = (res: {
    route?: string;
    helpscout?: { ok?: boolean; skipped?: string; error?: string };
    portal_notified: boolean;
  }): string => {
    const parts: string[] = [];
    if (res.portal_notified) parts.push("in-portal notice");
    if (res.route === "private") {
      parts.push("Help Scout email skipped (private route)");
    } else if (res.helpscout?.ok) {
      parts.push("Help Scout email sent");
    } else if (res.helpscout?.skipped) {
      parts.push(`Help Scout skipped (${res.helpscout.skipped})`);
    } else {
      parts.push(`Help Scout failed${res.helpscout?.error ? ` (${res.helpscout.error})` : ""}`);
    }
    return parts.join(", ");
  };

  const markAnswered = async (q: GatheringQuestion) => {
    const st = getEdit(q);
    const answer = st.answer.trim();
    if (!answer) {
      toast.error("Add an answer before sending it.");
      return;
    }
    setSavingId(q.id);
    try {
      const { error } = await supabase
        .from("congregation_questions")
        .update({
          status: "answered",
          answer_text: answer,
          answered_at: new Date().toISOString(),
          answered_by: user?.id ?? null,
          internal_notes: st.internal || null,
          call_reference: st.callRef || null,
        })
        .eq("id", q.id);
      if (error) throw error;

      let notice = "Answer saved.";
      try {
        const res = await triggerNotify(q.id);
        notice = `Answer saved. Delivery: ${describeDelivery(res)}.`;
      } catch (e: any) {
        console.error("notify failed:", e);
        notice = "Answer saved, but notification failed. Use 'Re-send' below.";
      }
      toast.success(notice);
      setEdits((e) => { const { [q.id]: _, ...rest } = e; return rest; });
      qc.invalidateQueries({ queryKey: ["admin-gathering-questions"] });
    } catch (err: any) {
      console.error("mark answered failed:", err);
      toast.error("The answer didn't save", { description: err?.message ?? "Try again." });
    } finally {
      setSavingId(null);
    }
  };

  const closeWithout = async (q: GatheringQuestion) => {
    const st = getEdit(q);
    setSavingId(q.id);
    try {
      const closureNote = st.answer.trim();
      const { error } = await supabase
        .from("congregation_questions")
        .update({
          status: "closed",
          answer_text: closureNote ? `Not taken up: ${closureNote}` : "Not taken up.",
          answered_at: new Date().toISOString(),
          answered_by: user?.id ?? null,
          internal_notes: st.internal || null,
          call_reference: st.callRef || null,
        })
        .eq("id", q.id);
      if (error) throw error;
      try { await triggerNotify(q.id); } catch (e) { console.error("notify (closed) failed:", e); }
      toast.success("Question closed.");
      setEdits((e) => { const { [q.id]: _, ...rest } = e; return rest; });
      qc.invalidateQueries({ queryKey: ["admin-gathering-questions"] });
    } catch (err: any) {
      console.error("close failed:", err);
      toast.error("Update didn't save", { description: err?.message ?? "Try again." });
    } finally {
      setSavingId(null);
    }
  };

  const savePrivateFields = async (q: GatheringQuestion) => {
    const st = getEdit(q);
    setSavingId(q.id);
    try {
      await persistAdminFields(q, { internal: st.internal, callRef: st.callRef, status: st.status });
      toast.success("Admin fields saved.");
      qc.invalidateQueries({ queryKey: ["admin-gathering-questions"] });
    } catch (err: any) {
      console.error("save admin fields failed:", err);
      toast.error("Save failed", { description: err?.message ?? "Try again." });
    } finally {
      setSavingId(null);
    }
  };

  const resend = async (q: GatheringQuestion) => {
    setNotifyingId(q.id);
    try {
      const res = await triggerNotify(q.id);
      toast.success(`Re-sent. Delivery: ${describeDelivery(res)}.`);
      qc.invalidateQueries({ queryKey: ["admin-gathering-questions"] });
    } catch (err: any) {
      console.error("resend failed:", err);
      toast.error("Re-send failed", { description: err?.message ?? "Try again." });
    } finally {
      setNotifyingId(null);
    }
  };

  // makePrivate now also creates the help desk ticket if one is not linked yet.
  const makePrivate = async (q: GatheringQuestion) => {
    setSavingId(q.id);
    try {
      const st = getEdit(q);
      const stamp = new Date().toISOString();
      const prefix = st.internal ? `${st.internal}\n\n` : "";
      const nextInternal = `${prefix}[${stamp}] Moved to private queue by admin.`;

      let ticketId = q.ticket_id;
      // Create a linked ticket + Help Scout sync when missing.
      if (!ticketId) {
        try {
          const profile = profileById.get(q.user_id);
          const subject = q.question_text.length > 80
            ? `${q.question_text.slice(0, 77)}...`
            : q.question_text;
          const { data: ticket, error: tErr } = await supabase
            .from("support_tickets")
            .insert({
              user_id: q.user_id,
              subject,
              message: q.question_text,
              category: "General",
              priority: "medium",
            })
            .select("id")
            .single();
          if (tErr) throw tErr;
          ticketId = ticket.id;

          // Non-blocking Help Scout sync
          supabase.functions
            .invoke("helpscout-ticket", {
              body: {
                ticketId: ticket.id,
                subject,
                message: q.question_text,
                userEmail: profile?.email || "",
                userName: profile?.full_name || "Member",
                category: "General",
                priority: "medium",
              },
            })
            .catch((e) => console.error("helpscout-ticket sync failed on makePrivate:", e));
        } catch (ticketErr) {
          console.error("private ticket create on move failed:", ticketErr);
        }
      }

      const { error } = await supabase
        .from("congregation_questions")
        .update({
          route: "private",
          internal_notes: nextInternal,
          ...(ticketId ? { ticket_id: ticketId } : {}),
        } as any)
        .eq("id", q.id);
      if (error) throw error;
      toast.success(ticketId ? "Moved to private queue. Help desk ticket opened." : "Moved to private queue.");
      setEdits((e) => { const { [q.id]: _, ...rest } = e; return rest; });
      qc.invalidateQueries({ queryKey: ["admin-gathering-questions"] });
      qc.invalidateQueries({ queryKey: ["admin-gathering-tickets"] });
    } catch (err: any) {
      console.error("makePrivate failed:", err);
      toast.error("Move failed", { description: err?.message ?? "Try again." });
    } finally {
      setSavingId(null);
    }
  };

  const renderMember = (userId: string) => {
    const p = profileById.get(userId);
    return p?.full_name || p?.email || "Member";
  };

  const renderInternalBlock = (q: GatheringQuestion) => {
    const st = getEdit(q);
    return (
      <div className="rounded-md border border-amber-300/40 bg-amber-50/40 p-3 space-y-2">
        <div className="flex items-center gap-2 text-xs font-semibold text-amber-900">
          <Lock className="w-3.5 h-3.5" />
          Internal only, never shown to the member
        </div>
        <div className="grid gap-2">
          <div>
            <Label htmlFor={`notes-${q.id}`} className="text-xs">Internal notes</Label>
            <Textarea
              id={`notes-${q.id}`}
              rows={2}
              value={st.internal}
              onChange={(e) => patchEdit(q.id, { internal: e.target.value })}
              placeholder="Context, decisions on the call, follow-up owners..."
            />
          </div>
          <div>
            <Label htmlFor={`callref-${q.id}`} className="text-xs">Call reference</Label>
            <Input
              id={`callref-${q.id}`}
              value={st.callRef}
              onChange={(e) => patchEdit(q.id, { callRef: e.target.value })}
              placeholder="e.g. Gathering 2026-07-24, minute 18"
            />
          </div>
        </div>
      </div>
    );
  };

  const renderStatusPicker = (q: GatheringQuestion) => {
    const st = getEdit(q);
    return (
      <Select
        value={st.status}
        onValueChange={async (v) => {
          const nextStatus = v as Status;
          patchEdit(q.id, { status: nextStatus });
          if ((q.status === "open" || q.status === "pending") && (nextStatus === "open" || nextStatus === "pending")) {
            try {
              await persistAdminFields(q, { status: nextStatus });
              qc.invalidateQueries({ queryKey: ["admin-gathering-questions"] });
            } catch (e: any) {
              toast.error("Status change failed", { description: e?.message });
            }
          }
        }}
      >
        <SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="open">{STATUS_LABEL.open}</SelectItem>
          <SelectItem value="pending">{STATUS_LABEL.pending}</SelectItem>
          <SelectItem value="answered" disabled>{STATUS_LABEL.answered} (use Send answer)</SelectItem>
          <SelectItem value="closed" disabled>{STATUS_LABEL.closed} (use Close)</SelectItem>
        </SelectContent>
      </Select>
    );
  };

  // Gathering active card: full composer (this is what the live call runs from).
  const renderGatheringCard = (q: GatheringQuestion) => {
    const st = getEdit(q);
    return (
      <Card key={q.id}>
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                {renderMember(q.user_id)}
                <Badge variant="outline" className="text-[10px]">
                  <Users className="w-3 h-3 mr-1" />Gathering
                </Badge>
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Submitted {formatDistanceToNow(new Date(q.created_at), { addSuffix: true })} ·{" "}
                {new Date(q.created_at).toLocaleString()}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{STATUS_LABEL[q.status]}</Badge>
              {renderStatusPicker(q)}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="rounded-md border bg-card p-3 whitespace-pre-wrap text-sm">
            {q.question_text}
          </div>

          <div>
            <Label htmlFor={`answer-${q.id}`} className="text-xs font-semibold">
              Answer to send to the member
            </Label>
            <Textarea
              id={`answer-${q.id}`}
              placeholder="Write the answer to send to this member..."
              rows={4}
              value={st.answer}
              onChange={(e) => patchEdit(q.id, { answer: e.target.value })}
            />
          </div>

          {renderInternalBlock(q)}

          <div className="flex flex-wrap gap-2 justify-end">
            <Button
              variant="ghost" size="sm"
              onClick={() => makePrivate(q)}
              disabled={savingId === q.id}
            >
              <Lock className="w-4 h-4 mr-1.5" />
              Make private
            </Button>
            <Button
              variant="ghost" size="sm"
              onClick={() => savePrivateFields(q)}
              disabled={savingId === q.id}
            >
              Save admin fields
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm">Close without answer</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Close this without an answer?</AlertDialogTitle>
                  <AlertDialogDescription>
                    The member will see it marked "Not taken up." If the answer field has text,
                    it will be shown to them as the reason, prefixed with "Not taken up:".
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Keep active</AlertDialogCancel>
                  <AlertDialogAction onClick={() => closeWithout(q)}>Close it</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button
              size="sm"
              onClick={() => markAnswered(q)}
              disabled={savingId === q.id || !st.answer.trim()}
            >
              {savingId === q.id ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" />Send answer</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Private card: record view. Answering happens in the help desk.
  const renderPrivateCard = (q: GatheringQuestion) => {
    const ticket = q.ticket_id ? ticketById.get(q.ticket_id) : null;
    const convoId = ticket?.helpscout_conversation_id || null;
    const helpscoutUrl = convoId ? `https://secure.helpscout.net/conversation/${convoId}/` : null;
    return (
      <Card key={q.id}>
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                {renderMember(q.user_id)}
                <Badge variant="outline" className="text-[10px]">
                  <Lock className="w-3 h-3 mr-1" />Private
                </Badge>
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Submitted {formatDistanceToNow(new Date(q.created_at), { addSuffix: true })} ·{" "}
                {new Date(q.created_at).toLocaleString()}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{STATUS_LABEL[q.status]}</Badge>
              {renderStatusPicker(q)}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="rounded-md border bg-card p-3 whitespace-pre-wrap text-sm">
            {q.question_text}
          </div>

          <div className="rounded-md border border-sky-300/40 bg-sky-50/40 p-3 text-sm text-sky-900 space-y-2">
            <div className="font-medium">Answering happens in the help desk</div>
            <p className="text-xs">
              Reply to this member in Help Scout. When you send the reply, the portal
              record updates automatically. No answer composer here on purpose.
            </p>
            {helpscoutUrl ? (
              <Button asChild size="sm" variant="outline">
                <a href={helpscoutUrl} target="_blank" rel="noreferrer">
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                  View in Help Scout
                </a>
              </Button>
            ) : q.ticket_id ? (
              <p className="text-xs italic">Help desk conversation is being created. Refresh in a moment.</p>
            ) : (
              <p className="text-xs italic">No help desk ticket linked yet.</p>
            )}
          </div>

          {renderInternalBlock(q)}

          <div className="flex flex-wrap gap-2 justify-end">
            <Button
              variant="ghost" size="sm"
              onClick={() => savePrivateFields(q)}
              disabled={savingId === q.id}
            >
              Save admin fields
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderGatheringList = () => {
    if (isLoading) return <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">Loading...</CardContent></Card>;
    if (gatheringQ.length === 0) {
      return (
        <Card>
          <CardContent className="py-10 flex flex-col items-center text-center">
            <MessageSquare className="w-8 h-8 text-muted-foreground mb-2" />
            <p className="text-sm">No gathering questions right now.</p>
          </CardContent>
        </Card>
      );
    }
    return <>{gatheringQ.map(renderGatheringCard)}</>;
  };

  const renderPrivateList = () => {
    if (isLoading) return <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">Loading...</CardContent></Card>;
    if (privateQ.length === 0) {
      return (
        <Card>
          <CardContent className="py-10 flex flex-col items-center text-center">
            <MessageSquare className="w-8 h-8 text-muted-foreground mb-2" />
            <p className="text-sm">No private questions right now.</p>
          </CardContent>
        </Card>
      );
    }
    return <>{privateQ.map(renderPrivateCard)}</>;
  };

  return (
    <div className="container max-w-5xl mx-auto px-4 py-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Users className="w-6 h-6" />
          Member Questions
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Everything members have asked, for the congregation and privately.
        </p>
      </div>

      <Tabs defaultValue="gathering">
        <TabsList>
          <TabsTrigger value="gathering">
            <Users className="w-3.5 h-3.5 mr-1.5" />
            For the congregation{gatheringQ.length ? ` (${gatheringQ.length})` : ""}
          </TabsTrigger>
          <TabsTrigger value="private">
            <Lock className="w-3.5 h-3.5 mr-1.5" />
            Private{privateQ.length ? ` (${privateQ.length})` : ""}
          </TabsTrigger>
          <TabsTrigger value="history">
            History{history.length ? ` (${history.length})` : ""}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="gathering" className="mt-4 space-y-3">
          <div className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
            The weekly call runs from this list. Submission order, oldest first.
          </div>
          {renderGatheringList()}
        </TabsContent>

        <TabsContent value="private" className="mt-4 space-y-3">
          <div className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
            Private questions are answered inside Help Scout. Portal records update
            automatically when a person replies.
          </div>
          {renderPrivateList()}
        </TabsContent>

        <TabsContent value="history" className="mt-4 space-y-3">
          {history.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                Nothing here yet.
              </CardContent>
            </Card>
          ) : (
            history.map((q) => {
              const st = getEdit(q);
              const ticket = q.ticket_id ? ticketById.get(q.ticket_id) : null;
              const convoId = ticket?.helpscout_conversation_id || null;
              const helpscoutUrl = convoId ? `https://secure.helpscout.net/conversation/${convoId}/` : null;
              return (
                <Card key={q.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <CardTitle className="text-base flex items-center gap-2">
                          {renderMember(q.user_id)}
                          <Badge variant="outline" className="text-[10px]">
                            {q.route === "private" ? (
                              <><Lock className="w-3 h-3 mr-1" />Private</>
                            ) : (
                              <><Users className="w-3 h-3 mr-1" />Gathering</>
                            )}
                          </Badge>
                        </CardTitle>
                        <p className="text-xs text-muted-foreground">
                          {STATUS_LABEL[q.status]}
                          {q.answered_at && ` · ${formatDistanceToNow(new Date(q.answered_at), { addSuffix: true })}`}
                          {q.notified_at
                            ? ` · Notified ${formatDistanceToNow(new Date(q.notified_at), { addSuffix: true })}`
                            : " · Not yet notified"}
                        </p>
                      </div>
                      <Badge variant={q.status === "answered" ? "default" : "outline"}>
                        {STATUS_LABEL[q.status]}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="rounded-md border bg-card p-3 whitespace-pre-wrap text-sm">
                      {q.question_text}
                    </div>
                    {q.answer_text && (
                      <div className="rounded-md bg-muted/60 p-3 whitespace-pre-wrap text-sm">
                        {q.answer_text}
                      </div>
                    )}
                    <div className="pt-2">
                      <Label className="text-xs font-semibold">Internal notes (admin only)</Label>
                      <Textarea
                        rows={2}
                        value={st.internal}
                        onChange={(e) => patchEdit(q.id, { internal: e.target.value })}
                      />
                      <Label className="text-xs font-semibold mt-2 block">Call reference (admin only)</Label>
                      <Input
                        value={st.callRef}
                        onChange={(e) => patchEdit(q.id, { callRef: e.target.value })}
                      />
                      <div className="flex justify-end gap-2 mt-2 flex-wrap">
                        {helpscoutUrl && (
                          <Button asChild variant="ghost" size="sm">
                            <a href={helpscoutUrl} target="_blank" rel="noreferrer">
                              <ExternalLink className="w-4 h-4 mr-1.5" />
                              View in Help Scout
                            </a>
                          </Button>
                        )}
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => savePrivateFields(q)}
                          disabled={savingId === q.id}
                        >
                          Save admin fields
                        </Button>
                        {q.status === "answered" && (
                          <Button
                            variant="outline" size="sm"
                            onClick={() => resend(q)}
                            disabled={notifyingId === q.id}
                          >
                            {notifyingId === q.id ? (
                              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                            ) : (
                              <><Send className="w-4 h-4 mr-2" />Re-send notification</>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
